import Point from 'ol/geom/Point';
import GeoJSON from 'ol/format/GeoJSON';
import { singleClick } from 'ol/events/condition';
import Feature from 'ol/Feature';

import { LayerProperty } from 'cadenza/map/layer-property';
import { logger } from 'cadenza/utils/logging';
import { MapProperty } from 'cadenza/map/map-property';
import { GeometryType, isMultiGeometryType } from 'cadenza/map/geom/geometry-type';
import { ajax } from 'cadenza/api-client/api';
import { MIMETYPE_JSON } from 'cadenza/model/media-type';
import { TooltipInteraction } from 'cadenza/map/custom/map-client-edit-geometry/tooltip-interaction';
import { context } from 'cadenza/map/context/context';
import { convertRectangleOrCircleGeometryIfNeeded } from 'cadenza/map/geom/geometry-converter';
import { roundScale } from 'cadenza/map/scale-utils';

import i18n from './messages.properties';

/*
 Please note that very similar logic is also included in WPS modules's AbstractGeometry.js
*/

export class GeometryFeatureSelectionInteraction extends TooltipInteraction {

  #drawLayer;
  #geometryType;
  #formatter;

  constructor (map, geometryType, drawLayer) {
    super(map, i18n('selectTooltip'), { handleEvent: event => this._handleEvent(event) });
    this.#geometryType = geometryType;
    this.#formatter = new GeoJSON();
    this.#drawLayer = drawLayer;
  }

  _handleEvent (event) {
    if (!singleClick(event)) {
      return true; // continue event propagation
    }
    const clickedCoordinate = event.coordinate;
    const clickedCoordinateAsPointGeometry = new Point(clickedCoordinate);
    const mapId = this.map.get(MapProperty.ID);
    const selectedLayer = getSelectedFeatureLayer(this.map, this.#geometryType);
    if (selectedLayer === null) {
      return false;
    }

    const geometryJson = this.#formatter.writeGeometryObject(clickedCoordinateAsPointGeometry);
    const existingGeometry = getLayerGeometry(this.#drawLayer, this.#geometryType);
    const existingGeometryJson = existingGeometry == null ? null : this.#formatter.writeGeometryObject(existingGeometry);
    const selectionMode = getSelectionMode(event);
    const mapSize = this.map.getSize();
    const mapWidth = mapSize[0];
    const mapHeight = mapSize[1];

    const layerId = selectedLayer.get(LayerProperty.ID);
    const data = JSON.stringify({
      layerIds: [ layerId ],
      geometryType: this.#geometryType,
      selectionGeometry: geometryJson,
      existingGeometry: existingGeometryJson,
      selectionMode,
      mapWidth,
      mapHeight
    });

    ajax(`/maps/${mapId}/layers/${layerId}/selection`, {
      type: 'POST',
      data,
      contentType: MIMETYPE_JSON
    })
      .then((selectedGeometry) => this._onGeometriesSelected(selectedGeometry, selectedLayer))
      .catch(error => logger.error(error));

    return false;
  }

  _onGeometriesSelected (selectedGeometry) {
    if (selectedGeometry && selectedGeometry.type === this.#geometryType) {
      const geometry = this.#formatter.readGeometry(selectedGeometry);
      setLayerGeometry(this.#drawLayer, geometry);
    } else {
      if (selectedGeometry.type.substring(0, 5) === 'Multi') {
        logger.error(i18n('clientGeometryEditor.InvalidMultiGeometryType'));
      } else {
        logger.error(i18n('clientGeometryEditor.InvalidSelectionGeometryType', {
          invalidGeometryType: selectedGeometry.type,
          validGeometryType: this.#geometryType
        }));
      }
    }
  }

}

export function getLayerGeometryFromFeatures (features, geometryType) {
  if (!(features instanceof Array) || features.length === 0) {
    return null;
  }
  const isMultiGeometry = isMultiGeometryType(geometryType);
  if (isMultiGeometry) {
    let multiGeometry = null;
    features.forEach(feature => {
      const featureGeometry = convertRectangleOrCircleGeometryIfNeeded(feature.getGeometry(), isMultiGeometry);
      if (multiGeometry) {
        if (geometryType === GeometryType.MULTI_POINT) {
          multiGeometry.appendPoint(featureGeometry.getPoint(0));
        } else if (geometryType === GeometryType.MULTI_LINE_STRING) {
          multiGeometry.appendLineString(featureGeometry.getLineString(0));
        } else if (geometryType === GeometryType.MULTI_POLYGON) {
          multiGeometry.appendPolygon(featureGeometry.getPolygon(0));
        }
      } else {
        multiGeometry = featureGeometry;
      }
    });
    return multiGeometry;
  }
  const lastFeature = features[features.length - 1];
  return convertRectangleOrCircleGeometryIfNeeded(lastFeature.getGeometry(), isMultiGeometry);
}

export function getSelectedFeatureLayer (map, geometryType) {
  const selectedLayer = map.get(MapProperty.SELECTED_LAYER);
  if (selectedLayer != null
    && selectedLayer.get(LayerProperty.HAS_FEATURES)
    && selectedLayer.get(LayerProperty.GEOMETRY_TYPE) != null
    && selectedLayer.get(LayerProperty.GEOMETRY_TYPE).split(' ').includes(geometryType)) {
    return selectedLayer;
  }
  return null;
}

export function isValidEditScale (minEditScale, map) {
  if (!minEditScale) {
    return true;
  }
  const mapContext = context(map);
  const scale = roundScale(mapContext.mapScale, 2, 0);
  return !(minEditScale && scale > minEditScale);
}

function getLayerGeometry (layer, geometryType) {
  return getLayerGeometryFromFeatures(layer.getSource().getFeatures(), geometryType);
}

function setLayerGeometry (layer, geometry) {
  layer.getSource().clear({ fast: true });
  if (geometry != null) {
    layer.getSource().addFeature(new Feature({ geometry }));
  }
}

function getSelectionMode (event) {
  if (event.originalEvent.ctrlKey) {
    return 'TOGGLE';
  }
  return event.originalEvent.shiftKey ? 'REMOVE' : 'NORMAL';
}
